import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { ConversationSelectType } from '../../../../data/data/conversations/enums';
import { useConversationSelectTypeValues } from '../../../../data/data/conversations/select-values';
import { usePatientConversationItems } from '../../../../data/data/patients/use-patient-conversation-items';
import { usePatientItem } from '../../../../data/data/patients/use-patient-item';
import {
  CreateConversationItemDocument,
  GetConversationItemsDocument,
  GetPatientConversationItemsDocument,
} from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import { FormRow, FormSection } from '../../../../elements/form/form';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { InputAutoCompletePatientFieldController } from '../../../form/controllers/input-auto-complete-patient-field-controller';
import { useConversationCreateForm } from './form/use-conversation-create-form';

type ConversationCreateModalProps = {
  healthProviderId: string | null | undefined;
  onSaved?: (id: string) => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function ConversationCreateModal({
  healthProviderId,
  onSaved,
  onCancel,
  onClose,
}: ConversationCreateModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const { form, formIsValid } = useConversationCreateForm();

  const { data: conversationSelectTypeData } =
    useConversationSelectTypeValues();

  const patientId = form.watch('patientId');

  const { data: patientItemData } = usePatientItem(patientId);

  const conversationType = form.watch('conversationType');

  const { data: patientConversationItemsData } = usePatientConversationItems({
    healthProviderId,
    patientId,
  });

  useEffect(() => {
    form.setValue('conversationType', ConversationSelectType.EXISTING, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [patientId, form]);

  useEffect(() => {
    if (conversationType !== ConversationSelectType.EXISTING) {
      form.setValue('conversationId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (conversationType !== ConversationSelectType.NEW) {
      form.setValue('conversationTitle', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [conversationType, form]);

  const [createConverstationItem, { loading: createConverstationItemLoading }] =
    useMutation(CreateConversationItemDocument, {
      refetchQueries: [
        GetConversationItemsDocument,
        GetPatientConversationItemsDocument,
      ],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      if (values.conversationType === ConversationSelectType.EXISTING) {
        const conversationItem = patientConversationItemsData.find(
          option =>
            option.id === values.conversationId && values.conversationType
        );

        if (conversationItem) {
          onSaved?.(conversationItem.id);

          onClose?.();

          return;
        }
      }

      try {
        const result = await createConverstationItem({
          variables: {
            ...values,
            title: values.conversationTitle,
          },
        });

        if (result.data?.conversationCreate.conversation) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'conversation.conversation-create.success',
            }),
          });

          onSaved?.(result.data?.conversationCreate.conversation.id);

          onClose?.();
        } else {
          throw new Error(
            'An unknown error has occurred in the server respond.'
          );
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    intl,
    form,
    patientConversationItemsData,
    createConverstationItem,
    showAlertMessage,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const { getFullName } = useFormatters();

  return (
    <>
      <Modal
        title={
          <CustomFormattedMessage id="conversation.conversation-create.title" />
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            <Button
              icon={
                createConverstationItemLoading
                  ? ButtonIcon.LOADING
                  : ButtonIcon.NONE
              }
              disabled={!formIsValid || createConverstationItemLoading}
              onClick={onHandleSubmit}
            >
              <CustomFormattedMessage id="conversation.conversation-create.save" />
            </Button>
          </>
        }
      >
        <FormProvider {...form}>
          <FormRow>
            <InputAutoCompletePatientFieldController
              name="patientId"
              label={
                <>
                  <CustomFormattedMessage id="common.patient" />
                  <small>*</small>
                </>
              }
              placeholder={intl.formatMessage({
                id: 'common.patients-search-placeholders',
              })}
              option={getFullName(patientItemData)}
              optionVariables={{ healthProviderId }}
            />
          </FormRow>

          <FormSection>
            {!patientId ? (
              <p className="info">
                <CustomFormattedMessage id="common.conversation.select-patient" />
              </p>
            ) : (
              <>
                <FormRow>
                  <InputCheckboxController<ConversationSelectType>
                    type={InputCheckboxType.RADIO}
                    name="conversationType"
                    label={
                      <>
                        <CustomFormattedMessage id="common.conversation.conversation-type" />
                        <small>*</small>
                      </>
                    }
                    options={conversationSelectTypeData}
                  />
                </FormRow>

                <hr />

                {!conversationType ? (
                  <p className="info">
                    <CustomFormattedMessage id="common.conversation.select-conversation-type" />
                  </p>
                ) : (
                  <>
                    {conversationType === ConversationSelectType.EXISTING && (
                      <FormRow>
                        <InputCheckboxController
                          type={InputCheckboxType.RADIO}
                          name="conversationId"
                          label={
                            <>
                              <CustomFormattedMessage id="common.conversation.existing-conversation" />
                              <small>*</small>
                            </>
                          }
                          placeholder={intl.formatMessage({
                            id: 'common.conversation.no-existing-conversation',
                          })}
                          options={patientConversationItemsData.map(option => ({
                            key: option.id,
                            title: option.title,
                          }))}
                        />
                      </FormRow>
                    )}

                    {conversationType === ConversationSelectType.NEW && (
                      <FormRow>
                        <InputFieldController
                          type="text"
                          name="conversationTitle"
                          label={
                            <>
                              <CustomFormattedMessage id="common.conversation.new-conversation" />
                              <small>*</small>
                            </>
                          }
                          placeholder={intl.formatMessage({
                            id: 'common.conversation.new-conversation',
                          })}
                        />
                      </FormRow>
                    )}
                  </>
                )}
              </>
            )}
          </FormSection>
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
